import { Language } from '@/constructs/Language';
import { Country } from '@/constructs/Country';
import type {
  RegionMetadata,
  CountryMetadata
} from '@/constructs/LocaleSchema';
import { Region } from '@/constructs/Region';
import config from './config';

/** Contains metadata for all configured countries. */
const ALL_REGIONS = {
  [Region.NA]: {
    aliases: {
      [Language.EN]: 'North America',
      [Language.DE]: 'Nordamerika',
      [Language.ES]: 'América del Norte',
      [Language.FR]: 'Amérique du Nord'
    }
  },
  [Region.EMEA]: {
    aliases: {
      [Language.EN]: 'Europe, Middle East, and Africa',
      [Language.DE]: 'Europa, Naher Osten und Afrika',
      [Language.ES]: 'Europa, Medio Oriente y África',
      [Language.FR]: 'Europe, Moyen-Orient et Afrique'
    }
  },
  [Region.AFRICA]: { aliases: { [Language.EN]: 'Africa' } },
  [Region.EUROPE]: { aliases: { [Language.EN]: 'Europe' } },
  [Region.ME]: { aliases: { [Language.EN]: 'Middle East' } },
  [Region.APAC]: { aliases: { [Language.EN]: 'Asia Pacific' } },
  [Region.LATAM]: { aliases: { [Language.EN]: 'Latin America' } }
} as const satisfies RegionMetadata;

/** Contains metadata for all configured countries. */
const ALL_COUNTRIES = {
  // APAC
  [Country.AU]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'Australia' }
  },
  [Country.CN]: { regionID: Region.APAC, aliases: { [Language.EN]: 'China' } },
  [Country.HK]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'Hong Kong' }
  },
  [Country.ID]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'Indonesia' }
  },
  [Country.JP]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'Japan', [Language.JA]: '日本' }
  },
  [Country.KR]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'South Korea' }
  },
  [Country.NZ]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'New Zealand' }
  },
  [Country.PH]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'Philippines' }
  },
  [Country.SG]: {
    regionID: Region.APAC,
    aliases: { [Language.EN]: 'Singapore' }
  },
  [Country.TW]: { regionID: Region.APAC, aliases: { [Language.EN]: 'Taiwan' } },

  // EMEA
  [Country.AD]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Andorra' }
  },
  [Country.AE]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'United Arab Emirates' }
  },
  [Country.AL]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Albania' }
  },
  [Country.AT]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Austria', [Language.DE]: 'Österreich' }
  },
  [Country.BA]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Bosnia & Herzegovina' }
  },
  [Country.BE]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Belgium', [Language.NL]: 'België' }
  },
  [Country.BG]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Bulgaria' }
  },
  [Country.CH]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Switzerland' }
  },
  [Country.CY]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Cyprus' } },
  [Country.CZ]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Czech Republic' }
  },
  [Country.DE]: {
    regionID: Region.EMEA,
    aliases: {
      [Language.EN]: 'Germany',
      [Language.DE]: 'Deutschland',
      [Language.ES]: 'Alemania',
      [Language.FR]: 'Allemagne'
    }
  },
  [Country.DK]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Denmark' }
  },
  [Country.DZ]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Algeria' }
  },
  [Country.EE]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Estonia' }
  },
  [Country.ES]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Spain' } },
  [Country.FI]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Finland' }
  },
  [Country.FR]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'France', [Language.FR]: 'France' }
  },
  [Country.GB]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'United Kingdom' }
  },
  [Country.GR]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Greece' } },
  [Country.HR]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Croatia' }
  },
  [Country.HU]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Hungary' }
  },
  [Country.IE]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Ireland' }
  },
  [Country.IL]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Israel' } },
  [Country.IS]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Iceland' }
  },
  [Country.IT]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Italy', [Language.IT]: 'Italia' }
  },
  [Country.JE]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Jersey' } },
  [Country.KZ]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Kazakhstan' }
  },
  [Country.LT]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Lithuania' }
  },
  [Country.LU]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Luxembourg', [Language.FR]: 'Luxembourg' }
  },
  [Country.LV]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Latvia' } },
  [Country.MA]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Morocco' }
  },
  [Country.MC]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Monaco' } },
  [Country.MD]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Moldova' }
  },
  [Country.ME]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Montenegro' }
  },
  [Country.MK]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'North Macedonia' }
  },
  [Country.MT]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Malta' } },
  [Country.NL]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Netherlands', [Language.NL]: 'Nederland' }
  },
  [Country.NO]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Norway' } },
  [Country.PL]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Poland' } },
  [Country.PT]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Portugal' }
  },
  [Country.RE]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Réunion' }
  },
  [Country.RO]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Romania' }
  },
  [Country.RS]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Serbia' } },
  [Country.RU]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Russia' } },
  [Country.SE]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Sweden' } },
  [Country.SI]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Slovenia' }
  },
  [Country.SK]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Slovakia' }
  },
  [Country.TN]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'Tunisia' }
  },
  [Country.TR]: { regionID: Region.EMEA, aliases: { [Language.EN]: 'Turkey' } },
  [Country.ZA]: {
    regionID: Region.EMEA,
    aliases: { [Language.EN]: 'South Africa' }
  },

  // LATAM
  [Country.BR]: {
    regionID: Region.LATAM,
    aliases: { [Language.EN]: 'Brazil' }
  },
  [Country.CL]: { regionID: Region.LATAM, aliases: { [Language.EN]: 'Chile' } },
  [Country.CO]: {
    regionID: Region.LATAM,
    aliases: { [Language.EN]: 'Colombia' }
  },

  // NA
  [Country.CA]: {
    regionID: Region.NA,
    aliases: {
      [Language.EN]: 'Canada',
      [Language.FR]: 'Canada (Français)',
      [Language.DE]: 'Kanada',
      [Language.ES]: 'Canadá'
    }
  },
  [Country.US]: {
    regionID: Region.NA,
    aliases: {
      [Language.EN]: 'United States',
      [Language.DE]: 'Vereinigte Staaten',
      [Language.FR]: 'États-Unis',
      [Language.ES]: 'Estados Unidos'
    }
  }
} as const satisfies CountryMetadata;

/** Contains country-specific settings for Ahnu. */
const AHNU_COUNTRY_SETTINGS = {
  [Country.US]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://ahnu.com' } }
  }
};

/** Contains country-specific settings for UGG. */
const UGG_COUNTRY_SETTINGS = {
  [Country.GB]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.ugg.com/en/gb/' }
    }
  },
  [Country.US]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://www.ugg.com' } }
  }
};

/**
 * HOKA-specific country metadata that overrides the default EMEA region
 * assignments. HOKA's production site displays countries under separate
 * Africa, Europe, and Middle East sections rather than the combined EMEA.
 */
const HOKA_COUNTRIES: CountryMetadata = {
  ...ALL_COUNTRIES,

  // Africa
  [Country.DZ]: { ...ALL_COUNTRIES[Country.DZ], regionID: Region.AFRICA },
  [Country.MA]: { ...ALL_COUNTRIES[Country.MA], regionID: Region.AFRICA },
  [Country.RE]: { ...ALL_COUNTRIES[Country.RE], regionID: Region.AFRICA },
  [Country.TN]: { ...ALL_COUNTRIES[Country.TN], regionID: Region.AFRICA },
  [Country.ZA]: { ...ALL_COUNTRIES[Country.ZA], regionID: Region.AFRICA },

  // Europe (override EMEA → EUROPE for European countries)
  [Country.AD]: { ...ALL_COUNTRIES[Country.AD], regionID: Region.EUROPE },
  [Country.AL]: { ...ALL_COUNTRIES[Country.AL], regionID: Region.EUROPE },
  [Country.AT]: { ...ALL_COUNTRIES[Country.AT], regionID: Region.EUROPE },
  [Country.BA]: { ...ALL_COUNTRIES[Country.BA], regionID: Region.EUROPE },
  [Country.BE]: { ...ALL_COUNTRIES[Country.BE], regionID: Region.EUROPE },
  [Country.BG]: { ...ALL_COUNTRIES[Country.BG], regionID: Region.EUROPE },
  [Country.CH]: { ...ALL_COUNTRIES[Country.CH], regionID: Region.EUROPE },
  [Country.CY]: { ...ALL_COUNTRIES[Country.CY], regionID: Region.EUROPE },
  [Country.CZ]: { ...ALL_COUNTRIES[Country.CZ], regionID: Region.EUROPE },
  [Country.DE]: { ...ALL_COUNTRIES[Country.DE], regionID: Region.EUROPE },
  [Country.DK]: { ...ALL_COUNTRIES[Country.DK], regionID: Region.EUROPE },
  [Country.EE]: { ...ALL_COUNTRIES[Country.EE], regionID: Region.EUROPE },
  [Country.ES]: { ...ALL_COUNTRIES[Country.ES], regionID: Region.EUROPE },
  [Country.FI]: { ...ALL_COUNTRIES[Country.FI], regionID: Region.EUROPE },
  [Country.FR]: { ...ALL_COUNTRIES[Country.FR], regionID: Region.EUROPE },
  [Country.GB]: { ...ALL_COUNTRIES[Country.GB], regionID: Region.EUROPE },
  [Country.GR]: { ...ALL_COUNTRIES[Country.GR], regionID: Region.EUROPE },
  [Country.HR]: { ...ALL_COUNTRIES[Country.HR], regionID: Region.EUROPE },
  [Country.HU]: { ...ALL_COUNTRIES[Country.HU], regionID: Region.EUROPE },
  [Country.IE]: { ...ALL_COUNTRIES[Country.IE], regionID: Region.EUROPE },
  [Country.IS]: { ...ALL_COUNTRIES[Country.IS], regionID: Region.EUROPE },
  [Country.IT]: { ...ALL_COUNTRIES[Country.IT], regionID: Region.EUROPE },
  [Country.JE]: { ...ALL_COUNTRIES[Country.JE], regionID: Region.EUROPE },
  [Country.KZ]: { ...ALL_COUNTRIES[Country.KZ], regionID: Region.EUROPE },
  [Country.LT]: { ...ALL_COUNTRIES[Country.LT], regionID: Region.EUROPE },
  [Country.LU]: { ...ALL_COUNTRIES[Country.LU], regionID: Region.EUROPE },
  [Country.LV]: { ...ALL_COUNTRIES[Country.LV], regionID: Region.EUROPE },
  [Country.MC]: { ...ALL_COUNTRIES[Country.MC], regionID: Region.EUROPE },
  [Country.MD]: { ...ALL_COUNTRIES[Country.MD], regionID: Region.EUROPE },
  [Country.ME]: { ...ALL_COUNTRIES[Country.ME], regionID: Region.EUROPE },
  [Country.MK]: { ...ALL_COUNTRIES[Country.MK], regionID: Region.EUROPE },
  [Country.MT]: { ...ALL_COUNTRIES[Country.MT], regionID: Region.EUROPE },
  [Country.NL]: { ...ALL_COUNTRIES[Country.NL], regionID: Region.EUROPE },
  [Country.NO]: { ...ALL_COUNTRIES[Country.NO], regionID: Region.EUROPE },
  [Country.PL]: { ...ALL_COUNTRIES[Country.PL], regionID: Region.EUROPE },
  [Country.PT]: { ...ALL_COUNTRIES[Country.PT], regionID: Region.EUROPE },
  [Country.RO]: { ...ALL_COUNTRIES[Country.RO], regionID: Region.EUROPE },
  [Country.RS]: { ...ALL_COUNTRIES[Country.RS], regionID: Region.EUROPE },
  [Country.SE]: { ...ALL_COUNTRIES[Country.SE], regionID: Region.EUROPE },
  [Country.SI]: { ...ALL_COUNTRIES[Country.SI], regionID: Region.EUROPE },
  [Country.SK]: { ...ALL_COUNTRIES[Country.SK], regionID: Region.EUROPE },

  // Middle East
  [Country.AE]: { ...ALL_COUNTRIES[Country.AE], regionID: Region.ME },
  [Country.IL]: { ...ALL_COUNTRIES[Country.IL], regionID: Region.ME },
  [Country.TR]: { ...ALL_COUNTRIES[Country.TR], regionID: Region.ME }
};

/** Contains country-specific settings for HOKA. */
const HOKA_COUNTRY_SETTINGS = {
  // NA
  [Country.US]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://www.hoka.com' } }
  },
  [Country.CA]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ca/' },
      [Language.FR]: { enabled: true, url: 'https://www.hoka.com/fr/ca/' }
    }
  },

  // LATAM
  [Country.BR]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/br/' }
    }
  },
  [Country.CL]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/cl/' }
    }
  },
  [Country.CO]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/co/' }
    }
  },

  // AFRICA
  [Country.DZ]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/dz/' }
    }
  },
  [Country.MA]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ma/' }
    }
  },
  [Country.RE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/re/' }
    }
  },
  [Country.TN]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/tn/' }
    }
  },
  [Country.ZA]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/za/' }
    }
  },

  // EUROPE
  [Country.AL]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/al/' }
    }
  },
  [Country.AD]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ad/' }
    }
  },
  [Country.AT]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/at/' }
    }
  },
  [Country.BA]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ba/' }
    }
  },
  [Country.BE]: {
    enabled: true,
    defaultLanguage: Language.NL,
    languages: {
      [Language.NL]: { enabled: true, url: 'https://www.hoka.com/nl/be/' }
    }
  },
  [Country.BG]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/bg/' }
    }
  },
  [Country.HR]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/hr/' }
    }
  },
  [Country.CY]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/cy/' }
    }
  },
  [Country.CZ]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/cz/' }
    }
  },
  [Country.DK]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/dk/' }
    }
  },
  [Country.EE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ee/' }
    }
  },
  [Country.FI]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/fi/' }
    }
  },
  [Country.FR]: {
    enabled: true,
    defaultLanguage: Language.FR,
    languages: {
      [Language.FR]: { enabled: true, url: 'https://www.hoka.com/fr/fr/' }
    }
  },
  [Country.DE]: {
    enabled: true,
    defaultLanguage: Language.DE,
    languages: {
      [Language.DE]: { enabled: true, url: 'https://www.hoka.com/de/de/' }
    }
  },
  [Country.GB]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/gb/' }
    }
  },
  [Country.GR]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/gr/' }
    }
  },
  [Country.HU]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/hu/' }
    }
  },
  [Country.IS]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/is/' }
    }
  },
  [Country.IE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ie/' }
    }
  },
  [Country.IT]: {
    enabled: true,
    defaultLanguage: Language.IT,
    languages: {
      [Language.IT]: { enabled: true, url: 'https://www.hoka.com/it/it/' }
    }
  },
  [Country.JE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/je/' }
    }
  },
  [Country.LV]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/lv/' }
    }
  },
  [Country.LT]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/lt/' }
    }
  },
  [Country.LU]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/lu/' }
    }
  },
  [Country.MT]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/mt/' }
    }
  },
  [Country.MC]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/mc/' }
    }
  },
  [Country.MD]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/md/' }
    }
  },
  [Country.ME]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/me/' }
    }
  },
  [Country.NL]: {
    enabled: true,
    defaultLanguage: Language.NL,
    languages: {
      [Language.NL]: { enabled: true, url: 'https://www.hoka.com/nl/nl/' }
    }
  },
  [Country.MK]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/mk/' }
    }
  },
  [Country.NO]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/no/' }
    }
  },
  [Country.PL]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/pl/' }
    }
  },
  [Country.PT]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/pt/' }
    }
  },
  [Country.RO]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ro/' }
    }
  },
  [Country.RS]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/rs/' }
    }
  },
  [Country.SK]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/sk/' }
    }
  },
  [Country.SI]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/si/' }
    }
  },
  [Country.ES]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/es/' }
    }
  },
  [Country.SE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/se/' }
    }
  },
  [Country.CH]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ch/' }
    }
  },
  [Country.KZ]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/kz/' }
    }
  },

  // MIDDLE EAST
  [Country.AE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ae/' }
    }
  },
  [Country.IL]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/il/' }
    }
  },
  [Country.TR]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/tr/' }
    }
  },

  // APAC
  [Country.AU]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/au/' }
    }
  },
  [Country.CN]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/cn/' }
    }
  },
  [Country.ID]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/id/' }
    }
  },
  [Country.JP]: {
    enabled: true,
    defaultLanguage: Language.JA,
    languages: {
      [Language.JA]: { enabled: true, url: 'https://www.hoka.com/ja/jp/' }
    }
  },
  [Country.NZ]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/nz/' }
    }
  },
  [Country.PH]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.hoka.com/en/ph/' }
    }
  }
};

/** Contains country-specific settings for Teva. */
const TEVA_COUNTRY_SETTINGS = {
  [Country.AU]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://au.teva.com/?locale=en_AU' }
    }
  },
  [Country.HK]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://hk.teva.com' } }
  },
  [Country.NZ]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://nz.teva.com' } }
  },
  [Country.SG]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva.com.sg' }
    }
  },
  [Country.TW]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://teva.com.tw' } }
  },
  [Country.KR]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://www.teva.kr' } }
  },
  [Country.JP]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://jp.teva.com' } }
  },
  [Country.BE]: {
    enabled: true,
    defaultLanguage: Language.NL,
    languages: {
      [Language.NL]: { enabled: true, url: 'https://www.teva-eu.com/nl/be/' }
    }
  },
  [Country.BG]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/bg/' }
    }
  },
  [Country.HR]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/hr/' }
    }
  },
  [Country.CZ]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/cz/' }
    }
  },
  [Country.DK]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/dk/' }
    }
  },
  [Country.EE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/ee/' }
    }
  },
  [Country.FI]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/fi/' }
    }
  },
  [Country.FR]: {
    enabled: true,
    defaultLanguage: Language.FR,
    languages: {
      [Language.FR]: { enabled: true, url: 'https://www.teva-eu.com/fr/fr/' }
    }
  },
  [Country.DE]: {
    enabled: true,
    defaultLanguage: Language.DE,
    languages: {
      [Language.DE]: { enabled: true, url: 'https://www.teva-eu.com/en/de/' }
    }
  },
  [Country.GR]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/gr/' }
    }
  },
  [Country.HU]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/hu/' }
    }
  },
  [Country.IE]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/ie/' }
    }
  },
  [Country.IT]: {
    enabled: true,
    defaultLanguage: Language.IT,
    languages: {
      [Language.IT]: {
        enabled: true,
        url: 'https://www.tevafootwear.it/?locale=it_IT'
      }
    }
  },
  [Country.LT]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/lt/' }
    }
  },
  [Country.LU]: {
    enabled: true,
    defaultLanguage: Language.FR,
    languages: {
      [Language.FR]: { enabled: true, url: 'https://www.teva-eu.com/en/lu/' }
    }
  },
  [Country.NL]: {
    enabled: true,
    defaultLanguage: Language.NL,
    languages: {
      [Language.NL]: { enabled: true, url: 'https://www.teva-eu.com/nl/nl/' }
    }
  },
  [Country.PL]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/pl/' }
    }
  },
  [Country.PT]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/pt/' }
    }
  },
  [Country.RO]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/ro/' }
    }
  },
  [Country.CH]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/ch/' }
    }
  },
  [Country.SI]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/si/' }
    }
  },
  [Country.SK]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/sk/' }
    }
  },
  [Country.ES]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/es/' }
    }
  },
  [Country.GB]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.teva-eu.com/en/gb/' }
    }
  },
  [Country.CA]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://www.tevaonline.ca' },
      [Language.FR]: { enabled: true, url: 'https://www.tevaonline.ca' }
    }
  },
  [Country.US]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://www.teva.com' } }
  }
};

/** Contains country-specific settings for mock purposes. */
const MOCK_COUNTRY_SETTINGS = {
  [Country.US]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: { [Language.EN]: { enabled: true, url: 'https://ahnu.com' } }
  },
  [Country.CA]: {
    enabled: true,
    defaultLanguage: Language.EN,
    languages: {
      [Language.EN]: { enabled: true, url: 'https://ahnu.ca/en_CA' },
      [Language.FR]: { enabled: true, url: 'https://ahnu.ca/fr_CA' }
    }
  },
  [Country.DE]: {
    enabled: true,
    defaultLanguage: Language.DE,
    languages: {
      [Language.DE]: { enabled: true, url: 'https://ahnu.com/de_DE' }
    }
  }
};

/**
 * The list of countries that are presented to the user by default.
 */
const DEFAULT_COUNTRIES_LIST = [
  Country.US,
  Country.GB
] as const satisfies Array<Country>;
/**
 * The list of countries that are presented to the user by default.
 */
const DEFAULT_COUNTRIES_LIST_TEVA = [
  Country.BE,
  Country.CA,
  Country.FR,
  Country.DE,
  Country.NL,
  Country.GB,
  Country.JP
] as const satisfies Array<Country>;

/**
 * The list of countries that are presented to the HOKA user by default.
 */
const DEFAULT_COUNTRIES_LIST_HOKA = [
  Country.US,
  Country.GB,
  Country.CA,
  Country.FR,
  Country.DE,
  Country.AU,
  Country.JP
] as const satisfies Array<Country>;

const locale = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      defaultCountry: Country.US,
      countries: ALL_COUNTRIES,
      regions: ALL_REGIONS,
      defaultCountries: DEFAULT_COUNTRIES_LIST,

      // TODO: Remove this after QA/UAT are done
      countrySettings: MOCK_COUNTRY_SETTINGS // Each site should overwrite this.
    }
  },
  'AHNU-US': { default: { countrySettings: AHNU_COUNTRY_SETTINGS } },
  'TEVA-US': {
    default: {
      countrySettings: TEVA_COUNTRY_SETTINGS,
      defaultCountries: DEFAULT_COUNTRIES_LIST_TEVA
    }
  },
  'UGG-US': { default: { countrySettings: UGG_COUNTRY_SETTINGS } },
  'HOKA-US': {
    default: {
      countries: HOKA_COUNTRIES,
      countrySettings: HOKA_COUNTRY_SETTINGS,
      defaultCountries: DEFAULT_COUNTRIES_LIST_HOKA
    }
  }
});

export default locale;
